import {
  IconSearch,
  Inline,
  Notice,
  Stack,
  Text,
  TextField,
  TextLink,
} from 'braid-design-system';
import { useEffect, useRef, useState } from 'react';
import { InlineCode } from 'scoobie';

import { InfiniteScrollingList } from '#src/components/InfiniteScrollingList/InfiniteScrollingList.tsx';
import {
  PaginationDirectionPicker,
  type PaginationDirectionState,
  usePaginationDirectionState,
} from '#src/components/PaginationDirectionPicker/PaginationDirectionPicker.tsx';
import { Query } from '#src/components/Query/Query.tsx';

import type {
  WebhookRequestByIdQuery,
  WebhookRequestByIdQueryVariables,
  WebhookRequestsForSubscriptionQuery,
  WebhookRequestsForSubscriptionQueryVariables,
} from '#src/types/generated/graphql.ts';

import { useTracking } from '#src/hooks/tracking/TrackingProvider.tsx';
import { useDebounce } from 'use-debounce';
import type { DescriptionCode } from './DescriptionCode.ts';
import {
  DescriptionCodePicker,
  useDescriptionCodeState,
} from './DescriptionCodePicker.tsx';
import { WebhookRequest } from './WebhookRequest.tsx';
import {
  GET_WEBHOOK_REQUESTS_FOR_SUBSCRIPTION,
  GET_WEBHOOK_REQUEST_BY_ID,
} from './queries.ts';

const REQUEST_ID_REGEXP =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const FilteredWebhookRequestList = ({
  subscriptionId,
  paginationDirectionState: { firstOrLast, processPageInfo },
  descriptionCodes,
}: {
  subscriptionId: string;
  paginationDirectionState: PaginationDirectionState;
  descriptionCodes: DescriptionCode[];
}) => (
  <Query<
    WebhookRequestsForSubscriptionQuery,
    WebhookRequestsForSubscriptionQueryVariables
  >
    errorMessage="We couldn’t load your subscription’s requests."
    query={GET_WEBHOOK_REQUESTS_FOR_SUBSCRIPTION}
    variables={{
      [firstOrLast]: 10,
      subscriptionId,
      filter: {
        descriptionCodes,
      },
    }}
    notifyOnNetworkStatusChange={false}
  >
    {({ data, fetchMore }) => {
      const { edges, pageInfo } = data.webhookRequestsForSubscription;

      const { shouldLoadMore, variables } = processPageInfo(pageInfo);

      const loadMore = () => fetchMore({ variables });

      return (
        <InfiniteScrollingList
          loadMore={loadMore}
          shouldLoadMore={shouldLoadMore}
          isEmpty={edges.length === 0}
          subject="requests"
          items={edges.map(({ node }) => (
            <WebhookRequest request={node} key={node.requestId} />
          ))}
        />
      );
    }}
  </Query>
);

const WebhookRequestByIdResult = ({
  subscriptionId,
  requestId,
  result: { webhookRequest },
}: {
  subscriptionId: string;
  requestId: string;
  result: WebhookRequestByIdQuery;
}) => {
  // Check that both the request & subscription exist.
  // If the subscription has been deleted there's not much we can do in our
  // subscription-specific UI.
  if (!webhookRequest) {
    return <Text tone="secondary">Request not found.</Text>;
  }

  const { webhookSubscription } = webhookRequest;

  if (!webhookSubscription) {
    return (
      <Notice tone="critical">
        <Text>
          Request <InlineCode>{requestId}</InlineCode> was made by a deleted
          subscription.
        </Text>
      </Notice>
    );
  }

  // Make sure the request belongs to this subscription
  if (webhookSubscription.id.value !== subscriptionId) {
    const subscriptionLink = `/manage/webhooks/${encodeURIComponent(
      webhookSubscription.id.value,
    )}`;

    return (
      <Notice tone="info">
        <Text>
          Request <InlineCode>{requestId}</InlineCode> was made by{' '}
          <TextLink href={subscriptionLink}>another subscription</TextLink>.
        </Text>
      </Notice>
    );
  }

  return <WebhookRequest request={webhookRequest} />;
};

const WebhookRequestById = ({
  subscriptionId,
  requestId,
}: {
  subscriptionId: string;
  requestId: string;
}) => (
  <Query<WebhookRequestByIdQuery, WebhookRequestByIdQueryVariables>
    errorMessage="We couldn’t load the request."
    query={GET_WEBHOOK_REQUEST_BY_ID}
    variables={{
      schemeId: subscriptionId.split(':')[0],
      requestId,
    }}
  >
    {({ data }) => (
      <WebhookRequestByIdResult
        subscriptionId={subscriptionId}
        requestId={requestId}
        result={data}
      />
    )}
  </Query>
);

interface Props {
  subscriptionId: string;
}

export const WebhookRequestList = ({ subscriptionId }: Props) => {
  const descriptionCodeState = useDescriptionCodeState();
  const paginationDirectionState = usePaginationDirectionState();
  const [requestId, setRequestId] = useState<string>('');
  const [debouncedRequestId] = useDebounce(requestId, 500);

  const { descriptionCodes } = descriptionCodeState;

  const normalisedRequestId = requestId.trim().toLocaleLowerCase();

  const invalidRequestId =
    normalisedRequestId && !REQUEST_ID_REGEXP.test(normalisedRequestId);

  const { trackLink } = useTracking();
  const trackLinkRef = useRef(trackLink);

  useEffect(() => {
    if (debouncedRequestId) {
      trackLinkRef.current({
        eventName: 'developer_site_lookup_succeeded',
        data: {
          lookupString: debouncedRequestId,
        },
      });
    }
  }, [debouncedRequestId]);

  return (
    <Stack space="small">
      <Stack space="gutter">
        <Inline align="right" alignY="center" space="gutter">
          <DescriptionCodePicker {...descriptionCodeState} />

          <PaginationDirectionPicker {...paginationDirectionState} />
        </Inline>

        <TextField
          id="request-id-search"
          icon={<IconSearch />}
          type="search"
          value={requestId}
          onChange={(event) => setRequestId(event.currentTarget.value)}
          onClear={() => setRequestId('')}
          placeholder="Look up request by ID"
          aria-label="Look up request by ID"
          message={invalidRequestId ? 'Request ID must be a UUID' : undefined}
          tone={invalidRequestId ? 'critical' : 'neutral'}
        />
      </Stack>

      {normalisedRequestId && !invalidRequestId ? (
        <WebhookRequestById
          subscriptionId={subscriptionId}
          requestId={normalisedRequestId}
        />
      ) : (
        <FilteredWebhookRequestList
          subscriptionId={subscriptionId}
          paginationDirectionState={paginationDirectionState}
          descriptionCodes={descriptionCodes}
        />
      )}
    </Stack>
  );
};
